import { useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { FileColumnSpec, FileRef, TableInput, TableInputRow as Row, columnInput, isFileColumn, traceKey } from '@/reports/inputs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NumberControl, cellInput, cellSelect } from './fields';
import { FileControl, WaveformControl, captureColumns } from './fileFields';
import { useFill } from './fillContext';

const IMAGE_ACCEPT = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

/** One file column's cell: its uploads, or its capture with the trace the server read from it, as in a sweep cell. */
function FileCell({ tableId, row, column, value, onChange }: { tableId: string; row: number; column: FileColumnSpec; value: Row[string]; onChange: (value: FileRef[] | FileRef | null) => void }) {
  const { editable, traces } = useFill();
  const input = columnInput(column);
  if (!editable && (value === null || value === undefined || (Array.isArray(value) && value.length === 0))) return <span className="px-1">—</span>;
  if (input.kind === 'waveform') return <WaveformControl compact input={input} value={Array.isArray(value) ? null : (value as FileRef | null) ?? null} trace={traces[traceKey(tableId, String(row), column.id)]} onChange={onChange} />;
  return <FileControl compact value={Array.isArray(value) ? value : []} multiple={input.multiple} accept={input.kind === 'image' ? IMAGE_ACCEPT : input.accept} onChange={onChange} />;
}

/**
 * Rows of typed cells: one per instrument, say. The operator adds and removes
 * rows. A file column takes uploads in each row; `noFiles` leaves them empty
 * (a table's starting rows, which hold no files).
 */
export function TableControl({ id, input, value, onChange, noFiles }: { id?: string; input: TableInput; value: Row[]; onChange: (value: Row[]) => void; noFiles?: boolean }) {
  const { editable } = useFill();
  // an upload ends after the drop that started it: by then rows may have been typed in, removed or added, so a cell is set
  // on the rows as they are now, in the row it was dropped in, followed through its edits (`successor`). If that row was
  // removed meanwhile, its upload goes with it rather than into whichever row took its place
  const latest = useRef(value);
  latest.current = value;
  const successor = useRef(new WeakMap<Row, Row>());
  const setCell = (from: Row, column: string, cell: Row[string]) => {
    const rows = latest.current;
    let target: Row | undefined = from;
    while (target && !rows.includes(target)) target = successor.current.get(target);
    if (!target) return;
    const next = { ...target, [column]: cell };
    successor.current.set(target, next);
    latest.current = rows.map((x) => (x === target ? next : x));
    onChange(latest.current);
  };
  const blank = (): Row => Object.fromEntries(input.columns.map((c) => [c.id, c.type === 'image' || c.type === 'file' ? [] : null]));
  return (
    <div id={id} className="overflow-x-auto rounded-lg border-[1px]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted/50 text-left">
            {input.columns.map((c) => (
              <th key={c.id} scope="col" className="h-9 border-b-[1px] px-2.5 font-medium text-muted-foreground" style={c.width ? { width: c.width } : undefined}>
                {c.title}{!isFileColumn(c) && c.unit ? ` (${c.unit})` : ''}
                {c.type === 'waveform' && captureColumns(c) && <span className="block text-xs font-normal">{captureColumns(c)}</span>}
              </th>
            ))}
            {editable && <th className="w-8 border-b-[1px]" aria-label={I18n.get('rv2RowActions')} />}
          </tr>
        </thead>
        <tbody>
          {value.map((row, r) => (
            <tr key={r} className="border-b-[1px] last:border-b-0">
              {input.columns.map((c) => {
                const cell = row[c.id] ?? null;
                const label = `${c.title}, ${I18n.get('rv2RowNumber', { row: String(r + 1) })}`;
                if (isFileColumn(c)) {
                  return (
                    <td key={c.id} className="min-w-40 px-1.5 py-1">
                      {noFiles ? <span className="px-1 text-xs text-muted-foreground">{I18n.get('rv2FileCellLater')}</span> : <FileCell tableId={input.id} row={r} column={c} value={cell} onChange={(v) => setCell(row, c.id, v)} />}
                    </td>
                  );
                }
                if (!editable) return <td key={c.id} className="h-8 px-2.5">{cell === null || cell === '' ? '—' : <span className="font-medium">{String(cell)}</span>}</td>;
                const blank = cell === null || cell === '';
                return (
                  <td key={c.id} className="px-1.5 py-0">
                    {c.type === 'number' || c.type === 'integer' ? (
                      <NumberControl aria-label={label} value={typeof cell === 'number' ? cell : null} onChange={(v) => setCell(row, c.id, c.type === 'integer' && v !== null ? Math.round(v) : v)} placeholder={c.help ?? I18n.get('rv2TypeAValue')} inputClassName={cellInput(blank)} />
                    ) : c.type === 'choice' ? (
                      <select aria-label={label} value={cell === null ? '' : String(cell)} onChange={(e) => setCell(row, c.id, e.target.value || null)} className={cellSelect(blank)}>
                        <option value="">{I18n.get('selectDot')}</option>
                        {(c.choices ?? []).map((choice, j) => <option key={j} value={choice}>{choice}</option>)}
                      </select>
                    ) : (
                      <Input aria-label={label} value={cell === null ? '' : String(cell)} placeholder={c.help ?? I18n.get('rv2TypeAValue')} onChange={(e) => setCell(row, c.id, e.target.value === '' ? null : e.target.value)} className={cellInput(blank)} />
                    )}
                  </td>
                );
              })}
              {editable && (
                <td className="w-8 text-center">
                  <button type="button" className="inline-flex size-6 items-center justify-center rounded text-muted-foreground hover:text-foreground" aria-label={I18n.get('rv2RemoveRow', { row: String(r + 1) })} onClick={() => onChange(value.filter((_, i) => i !== r))}>
                    <X className="size-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {value.length === 0 && (
            <tr><td colSpan={input.columns.length + 1} className="h-[2.5rem] px-2.5 text-muted-foreground">{I18n.get('rv2NoRows')}</td></tr>
          )}
        </tbody>
      </table>
      {editable && (
        <div className="border-t-[1px] px-1.5 py-1">
          <Button variant="ghost" size="sm" onClick={() => onChange([...value, blank()])}><Plus /> {I18n.get('rv2AddRow')}</Button>
        </div>
      )}
    </div>
  );
}
